import React, { useState, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
import { settingsService } from '../../../lib/services';

const GeneralSettings = () => {
  const [siteName, setSiteName] = useState('webpages123.com');
  const [siteUrl, setSiteUrl] = useState('https://webpages123.com');
  const [adminEmail, setAdminEmail] = useState('admin@webpages123.com');
  const [timezone, setTimezone] = useState('UTC');
  const [language, setLanguage] = useState('en');
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [sslEnabled, setSslEnabled] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');

  const timezoneOptions = [
    { value: 'UTC', label: 'UTC' },
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'Europe/London', label: 'London (GMT)' },
    { value: 'Europe/Paris', label: 'Paris (CET)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
    { value: 'Australia/Sydney', label: 'Sydney (AEDT)' }
  ];

  const languageOptions = [
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Spanish' },
    { value: 'fr', label: 'French' },
    { value: 'de', label: 'German' },
    { value: 'ja', label: 'Japanese' }
  ];

  useEffect(() => {
    const loadSettings = async () => {
      setLoading(true);
      try {
        const { data, error } = await settingsService?.getByCategory('general');
        if (!error && data) {
          data?.forEach(setting => {
            if (setting?.settingKey === 'site_name') setSiteName(setting?.settingValue || 'webpages123.com');
            if (setting?.settingKey === 'contact_email') setAdminEmail(setting?.settingValue || '');
            if (setting?.settingKey === 'timezone') setTimezone(setting?.settingValue || 'UTC');
            if (setting?.settingKey === 'default_language') setLanguage(setting?.settingValue || 'en');
            if (setting?.settingKey === 'maintenance_mode') setMaintenanceMode(setting?.settingValue === 'true');
          });
        }
      } catch (err) {
        console.error('Failed to load settings:', err);
      } finally {
        setLoading(false);
      }
    };
    loadSettings();
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    setSaveSuccess(false);
    setSaveError('');
    try {
      const settingsToUpdate = [
        { settingKey: 'site_name', settingValue: siteName, settingType: 'string', category: 'general' },
        { settingKey: 'contact_email', settingValue: adminEmail, settingType: 'string', category: 'general' },
        { settingKey: 'timezone', settingValue: timezone, settingType: 'string', category: 'general' },
        { settingKey: 'default_language', settingValue: language, settingType: 'string', category: 'general' },
        { settingKey: 'maintenance_mode', settingValue: String(maintenanceMode), settingType: 'boolean', category: 'general' }
      ];
      const { error } = await settingsService?.upsert(settingsToUpdate);
      if (error) throw error;
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(err?.message || 'Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader2" size={24} className="text-primary animate-spin mr-2" />
        <span className="text-muted-foreground">Loading settings...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 md:space-y-8">
      {saveSuccess && (
        <div className="flex items-center gap-2 p-3 bg-success/10 border border-success/20 rounded-lg text-success text-sm">
          <Icon name="CheckCircle" size={16} />
          Settings saved successfully!
        </div>
      )}
      {saveError && (
        <div className="flex items-center gap-2 p-3 bg-error/10 border border-error/20 rounded-lg text-error text-sm">
          <Icon name="AlertCircle" size={16} />
          {saveError}
        </div>
      )}
      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        <div className="flex items-start gap-3 mb-4 md:mb-6">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon name="Globe" size={20} className="text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base md:text-lg font-semibold text-foreground mb-1">Site Information</h3>
            <p className="text-sm text-muted-foreground">Basic configuration for your website</p>
          </div>
        </div>

        <div className="space-y-4 md:space-y-5">
          <Input
            label="Site Name"
            type="text"
            value={siteName}
            onChange={(e) => setSiteName(e?.target?.value)}
            placeholder="Enter site name"
            required
          />
          <Input
            label="Site URL"
            type="url"
            value={siteUrl}
            onChange={(e) => setSiteUrl(e?.target?.value)}
            placeholder="https://example.com"
            description="Your primary domain URL"
            required
          />
          <Input
            label="Admin Email"
            type="email"
            value={adminEmail}
            onChange={(e) => setAdminEmail(e?.target?.value)}
            placeholder="admin@example.com"
            description="Primary contact for system notifications"
            required
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
            <Select
              label="Timezone"
              options={timezoneOptions}
              value={timezone}
              onChange={setTimezone}
              searchable
            />
            <Select
              label="Default Language"
              options={languageOptions}
              value={language}
              onChange={setLanguage}
            />
          </div>
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        <div className="flex items-start gap-3 mb-4 md:mb-6">
          <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center flex-shrink-0">
            <Icon name="Shield" size={20} className="text-warning" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base md:text-lg font-semibold text-foreground mb-1">Security & Status</h3>
            <p className="text-sm text-muted-foreground">Control site availability and security features</p>
          </div>
        </div>
        <div className="space-y-4">
          <Checkbox
            label="Enable SSL/HTTPS"
            description="Secure your site with HTTPS encryption"
            checked={sslEnabled}
            onChange={(e) => setSslEnabled(e?.target?.checked)}
          />
          <Checkbox
            label="Maintenance Mode"
            description="Display maintenance page to visitors while you work on the site"
            checked={maintenanceMode}
            onChange={(e) => setMaintenanceMode(e?.target?.checked)}
          />
        </div>
      </div>
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => window.location?.reload()}>Cancel</Button>
        <Button
          variant="default"
          loading={isSaving}
          iconName="Save"
          iconPosition="left"
          onClick={handleSave}
        >
          Save Changes
        </Button>
      </div>
    </div>
  );
};

export default GeneralSettings;